import { useContext, useMemo } from 'react';
import clsx from 'clsx';
import * as R from 'ramda';

import FavoritesListsTab from '@atom/components/common/favorites/FavoritesListsTab';
import AddTeamModalContext from '@atom/components/common/workOrderDetail/addTeamModal/AddTeamModalContext';
import { FavoriteDetail, FavoriteSubjectType } from '@atom/types/favorite';
import { UserDetail } from '@atom/types/user';

import classes from './usersTab.module.css';

const FavoritesTab = () => {
  const {
    favoriteUsers,
    favoritesLoading,
    favoritesError,
    favoritesLists,
    selectedUsers,
    setSelectedUsers,
    usersOnTask,
    updateFavorite,
    addUserToCart,
    removeUserFromCart,
    handleAddList,
    handleGetFavorites,
  } = useContext(AddTeamModalContext);

  const selectedFavoriteIds: Set<string> = useMemo(
    () => new Set(selectedUsers.map(user => user.id)),
    [selectedUsers],
  );

  const disabledFavoriteIds: Set<string> = useMemo(
    () => new Set([...usersOnTask]),
    [usersOnTask],
  );

  const handleFavoriteToggle = (event, favorite: FavoriteDetail) => {
    event.stopPropagation();
    const toggledUser = favoriteUsers.find(user => user.id === favorite.id);

    if (!toggledUser) {
      return;
    }

    if (selectedFavoriteIds.has(favorite.id)) {
      removeUserFromCart(toggledUser.id);
    }

    addUserToCart(toggledUser);
  };

  const handleSetSelectedFavoriteIds = (ids: Set<string>) => {
    const pool = [...selectedUsers, ...favoriteUsers];
    const newSelectedUsers = Array.from(ids)
      .map(id => pool.find(user => user.id === id))
      .filter((user): user is UserDetail => !R.isNil(user));

    setSelectedUsers(R.uniqBy(user => user.id, newSelectedUsers));
  };

  if (favoritesLists.length === 0) {
    return (
      <div className={clsx(classes.usersList, classes.emptyUsersList)}>
        Add your favorite Users to quickly find them again when working on any
        tasks. You can add up to 250 favorites in 50 lists.
      </div>
    );
  }

  return (
    <FavoritesListsTab
      favoritesLists={favoritesLists}
      subjectType={FavoriteSubjectType.USER}
      selectedFavoriteIds={selectedFavoriteIds}
      setSelectedFavoriteIds={handleSetSelectedFavoriteIds}
      disabledFavoriteIds={disabledFavoriteIds}
      handleFavoriteToggle={handleFavoriteToggle}
      updateFavorite={updateFavorite}
      allFavorites={favoriteUsers}
      handleAddList={handleAddList}
      canAddFavorite
      canAddList
      favoritesLoading={favoritesLoading}
      favoritesError={favoritesError}
      handleGetFavorites={handleGetFavorites}
    />
  );
};

export default FavoritesTab;
